// src/pages/AbonnementsRenew.js - VERSION FINALE CORRIGÉE
import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import PaiementNitaAmanaModal from "../components/PaiementNitaAmanaModal";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Compte à rebours jusqu'à l'expiration (24h) d'un code d'achat NITA/Amana
// (même composant que sur AbonnementsDetails.js / AbonnementsCreate.js).
const CompteARebours = ({ createdAt }) => {
  const [texte, setTexte] = useState('');

  useEffect(() => {
    const calculer = () => {
      const limite = new Date(createdAt.replace(' ', 'T')).getTime() + 24 * 60 * 60 * 1000;
      const restantMs = limite - Date.now();
      if (restantMs <= 0) {
        setTexte('Référence expirée');
        return;
      }
      const heures = Math.floor(restantMs / (1000 * 60 * 60));
      const minutes = Math.floor((restantMs % (1000 * 60 * 60)) / (1000 * 60));
      setTexte(`Temps restant pour payer : ${heures}h ${minutes}min`);
    };
    calculer();
    const interval = setInterval(calculer, 60000);
    return () => clearInterval(interval);
  }, [createdAt]);

  return <span>{texte}</span>;
};

const AbonnementsRenew = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [currentProfile, setCurrentProfile] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [abonnement, setAbonnement] = useState(null);
  const [offres, setOffres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingOffres, setLoadingOffres] = useState(true);
  const [loadingRenew, setLoadingRenew] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedOffre, setSelectedOffre] = useState(null);
  const [form, setForm] = useState({
    id_offre: "",
    numero_recu: "",
    mode_paiement: "manuel"
  });
  const [showPaiementModal, setShowPaiementModal] = useState(false);
  const [paiementEnCours, setPaiementEnCours] = useState(null); // { id_paiement, montant, newAbonnementId }
  const [paiementEnAttente, setPaiementEnAttente] = useState(null);
  const [annulationEnCours, setAnnulationEnCours] = useState(false);
  const [essaiBloque, setEssaiBloque] = useState(false);

  // Palette de couleurs SecureControl
  const COLORS = {
    primary: '#0B3C5D',
    secondary: '#1E88C8',
    accent: '#4FC3F7',
    darkText: '#2E2E2E',
    lightBg: '#F5F7FA',
    white: '#FFFFFF',
    danger: '#f44336',
    success: '#4caf50',
    warning: '#ff9800',
    info: '#2196f3'
  };

  useEffect(() => {
    // Récupérer le profil utilisateur
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        setCurrentProfile(String(user.profile));
        setCurrentUser(user);
      } catch (e) {
        console.error("Erreur parsing user:", e);
      }
    }

    if (id) {
      fetchAbonnementDetails();
      fetchOffres();
    }
  }, [id]);

  // Récupérer les détails de l'abonnement à renouveler
  const fetchAbonnementDetails = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setLoading(false);
        return;
      }

      const url = `${API_BASE_URL}/abonnements/get.php?id=${id}`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data?.success && response.data.abonnement) {
        const abonnementData = response.data.abonnement;
        setAbonnement(abonnementData);
        setPaiementEnAttente(abonnementData.paiement_en_attente || null);

        // Une période d'essai ne se "renouvelle" pas : il faut souscrire un
        // premier abonnement payant depuis la page de création (même règle
        // que le backend dans abonnements/renew.php).
        if (abonnementData.offre?.type_offre === 'essai') {
          setEssaiBloque(true);
        }

        // Vérifier les permissions
        if (currentProfile === "3" && abonnementData.id_societe != currentUser?.societe_id) {
          setError("Vous ne pouvez renouveler que les abonnements de votre société.");
          setLoading(false);
          return;
        }
      } else {
        setError(response.data?.message || "Abonnement non trouvé.");
      }
    } catch (err) {
      console.error("Erreur chargement abonnement:", err);
      
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403) {
        setError("Accès interdit. Vous n'avez pas la permission de renouveler cet abonnement.");
      } else if (err.response?.status === 404) {
        setError("Abonnement non trouvé.");
      } else {
        setError("Erreur lors du chargement des détails.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Reprend le paiement en attente (rouvre la modal avec le même id_paiement)
  const handleFinaliserPaiementEnAttente = () => {
    if (!paiementEnAttente) return;
    setPaiementEnCours({
      id_paiement: paiementEnAttente.id_paiement,
      montant: paiementEnAttente.montant,
      newAbonnementId: paiementEnAttente.id_abonnement,
    });
    setShowPaiementModal(true);
  };

  // Annule le paiement en attente pour permettre un nouveau renouvellement
  const handleAnnulerPaiementEnAttente = async () => {
    if (!paiementEnAttente) return;
    if (!window.confirm("Annuler ce paiement en attente ? Vous pourrez ensuite relancer le renouvellement.")) {
      return;
    }
    setAnnulationEnCours(true);
    try {
      const token = localStorage.getItem("access_token");
      await axios.post(
        `${API_BASE_URL}/abonnements/annuler_paiement.php`,
        { id_paiement: paiementEnAttente.id_paiement },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      setPaiementEnAttente(null);
    } catch (err) {
      setError(err.response?.data?.message || "Impossible d'annuler ce paiement.");
    } finally {
      setAnnulationEnCours(false);
    }
  };

  // Récupérer la liste des offres disponibles
  const fetchOffres = async () => {
    setLoadingOffres(true);
    try {
      const token = localStorage.getItem("access_token");
      const url = `${API_BASE_URL}/offres/list.php`;
      
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data?.success) {
        const offresData = response.data.offres || [];
        // Filtrer seulement les offres actives — l'essai n'est jamais une
        // offre de renouvellement valide (même règle que AbonnementsCreate.js).
        const offresActives = offresData.filter(offre => offre.statut_offre === 1 && offre.supprimer === 0 && offre.type_offre !== 'essai');
        setOffres(offresActives);
        
        // Sélectionner automatiquement l'offre actuelle si elle existe encore
        if (abonnement?.offre?.id_offre) {
          const offreActuelle = offresActives.find(o => o.id_offre == abonnement.offre.id_offre);
          if (offreActuelle) {
            setSelectedOffre(offreActuelle);
            setForm(prev => ({ ...prev, id_offre: offreActuelle.id_offre }));
          }
        }
      }
    } catch (error) {
      console.error("Erreur chargement offres:", error);
    } finally {
      setLoadingOffres(false);
    }
  };

  // Formater le prix en FCFA
  const formatPrix = (montant) => {
    const montantArrondi = Math.round(parseFloat(montant) || 0);
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(montantArrondi);
  };

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return "Non spécifié";
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Obtenir l'icône selon le type
  const getTypeIcon = (type) => {
    switch(type) {
      case 'annuel': return 'bi-calendar-year';
      case 'mensuel': return 'bi-calendar-month';
      case 'trimestriel': return 'bi-calendar3';
      case 'semestriel': return 'bi-calendar2-range';
      default: return 'bi-calendar';
    }
  };

  // Obtenir la couleur selon le type
  const getTypeColor = (type) => {
    switch(type) {
      case 'annuel': return '#0B3C5D';
      case 'mensuel': return '#1E88C8';
      case 'trimestriel': return '#4FC3F7';
      case 'semestriel': return '#2196f3';
      default: return '#6c757d';
    }
  };

  // Calculer le prix mensuel
  const calculerPrixMensuel = (montant, dureeMois) => {
    if (dureeMois === 0) return 0;
    return montant / dureeMois;
  };

  // Formater la durée
  const formatDuree = (mois) => {
    if (mois === 1) return "1 mois";
    if (mois < 12) return `${mois} mois`;
    if (mois === 12) return "1 an";
    const annees = Math.floor(mois / 12);
    const moisRestants = mois % 12;
    if (moisRestants === 0) return `${annees} an${annees > 1 ? 's' : ''}`;
    return `${annees} an${annees > 1 ? 's' : ''} et ${moisRestants} mois`;
  };

  // Gérer la sélection d'offre
  const handleSelectOffre = (offre) => {
    setSelectedOffre(offre);
    setForm(prev => ({ ...prev, id_offre: offre.id_offre }));
  };

  // Gérer les changements dans le formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  // Valider le formulaire
  const validateForm = () => {
    if (!form.id_offre) {
      setError("Veuillez sélectionner une offre.");
      return false;
    }
    
    if (currentProfile === "1" && form.mode_paiement === "manuel" && !form.numero_recu?.trim()) {
      setError("Le numéro de reçu est requis pour les paiements manuels.");
      return false;
    }
    
    return true;
  };

  // Gérer le renouvellement - VERSION CORRIGÉE AVEC REDIRECTION VERS LA LISTE
  const handleRenew = async () => {
    if (!validateForm()) return;
    
    setLoadingRenew(true);
    setError("");
    setSuccess("");

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setLoadingRenew(false);
        return;
      }

      // IMPORTANT: Vérifiez le nom exact du champ pour la société
      const societeId = abonnement.id_societe || abonnement.societe_id;
      
      if (!societeId) {
        setError("Impossible de déterminer l'ID de la société.");
        setLoadingRenew(false);
        return;
      }

      const dataToSend = {
        id_offre: parseInt(form.id_offre),
        societe_id: societeId,
        numero_recu: form.numero_recu?.trim() || "",
        mode_paiement: currentProfile === "1" ? "manuel" : "en_ligne"
      };

      const url = `${API_BASE_URL}/abonnements/renew.php`;
      
      const response = await axios.post(url, dataToSend, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data?.success) {
        const newAbonnementId = response.data.abonnement?.id_abonnement ||
                                response.data.abonnement?.id;
        const statutPaiement = response.data.abonnement?.statut_paiement;

        if (statutPaiement === 'pending') {
          // Gestionnaire : le renouvellement n'est qu'en attente tant que le
          // paiement NITA/AMANA n'est pas réellement confirmé — on ouvre la
          // modal de paiement au lieu d'annoncer un succès prématuré.
          setPaiementEnCours({
            id_paiement: response.data.id_paiement,
            montant: selectedOffre?.montant,
            newAbonnementId,
          });
          setShowPaiementModal(true);
          setLoadingRenew(false);
          return;
        }

        // Message de succès amélioré
        setSuccess(`✅ Abonnement renouvelé avec succès !

📋 Nouvelle référence: ${newAbonnementId}
🏢 Société: ${abonnement.nom_societe}
📦 Offre: ${selectedOffre?.libele_offre}
💰 Montant: ${formatPrix(selectedOffre?.montant)}
📅 Date début: ${formatDate(response.data.abonnement?.date_debut || response.data.abonnement?.date_debut_abonnemt)}
📅 Date fin: ${formatDate(response.data.abonnement?.date_fin || response.data.abonnement?.date_fin_abonnement)}

Redirection vers la liste des abonnements dans 5 secondes...`);

        // CORRECTION ICI : Redirection vers la liste des abonnements après 5 secondes
        setTimeout(() => {
          navigate("/abonnements");
        }, 5000);
      } else {
        setError(response.data?.message || "Erreur lors du renouvellement.");
      }
    } catch (err) {
      console.error("Erreur renouvellement:", err);
      
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
        localStorage.clear();
        setTimeout(() => navigate("/login"), 2000);
      } else if (err.response?.status === 403 && err.response?.data?.code === 'PAIEMENT_EN_ATTENTE') {
        setPaiementEnAttente(err.response.data.paiement_en_attente || paiementEnAttente);
        setError(err.response.data.message);
      } else if (err.response?.status === 403 && err.response?.data?.code === 'ESSAI_NON_RENOUVELABLE') {
        setEssaiBloque(true);
        setError(err.response.data.message);
      } else if (err.response?.status === 403) {
        setError("Accès interdit. Permissions insuffisantes.");
      } else if (err.response?.status === 404) {
        setError("Offre non disponible.");
      } else if (err.response?.status === 400) {
        const errorMsg = err.response.data?.message || "Données invalides";
        setError(`Erreur validation: ${errorMsg}`);
      } else if (err.response?.data?.message) {
        setError(`Erreur serveur: ${err.response.data.message}`);
      } else if (!err.response) {
        setError("Serveur indisponible. Vérifiez votre connexion.");
      } else {
        setError("Erreur lors du renouvellement de l'abonnement.");
      }
    } finally {
      setLoadingRenew(false);
    }
  };

  // Effacer le message de succès après 10 secondes
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess("");
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Renouveler l'abonnement</h1>
        </div>
        <section className="section">
          <div className="text-center py-5">
            <div className="spinner-border" role="status" style={{ color: COLORS.primary, width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-3" style={{ color: COLORS.darkText }}>
              Chargement des informations...
            </p>
          </div>
        </section>
      </Layout>
    );
  }

  if (error && !abonnement) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Renouveler l'abonnement</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/dashboard" style={{ color: COLORS.secondary }}>
                  Dashboard
                </Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/abonnements" style={{ color: COLORS.secondary }}>
                  Abonnements
                </Link>
              </li>
              <li className="breadcrumb-item active" style={{ color: COLORS.primary }}>Renouveler</li>
            </ol>
          </nav>
        </div>

        <section className="section">
          <div className="card" style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="mb-4">
                <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#ffebee',
                  color: COLORS.danger
                }}>
                  <i className="bi bi-exclamation-triangle fs-1"></i>
                </div>
              </div>
              <h5 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>Erreur</h5>
              <p className="text-muted mb-4" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                {error}
              </p>
              
              <div className="d-flex justify-content-center gap-3">
                <button
                  className="btn fw-semibold"
                  onClick={() => navigate("/abonnements")}
                  style={{
                    color: COLORS.primary,
                    border: `2px solid ${COLORS.primary}`,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.color = COLORS.white;
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = COLORS.primary;
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour aux abonnements
                </button>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  // Une période d'essai ne se "renouvelle" pas — il faut souscrire un premier
  // abonnement payant depuis la page de création.
  if (essaiBloque) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Renouveler l'abonnement</h1>
        </div>

        <section className="section">
          <div className="card" style={{
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            border: 'none'
          }}>
            <div className="card-body text-center py-5">
              <div className="mb-4">
                <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{
                  width: '80px',
                  height: '80px',
                  backgroundColor: '#EDE7F6',
                  color: '#6D28D9'
                }}>
                  <i className="bi bi-stars fs-1"></i>
                </div>
              </div>
              <h5 className="mb-3" style={{ color: COLORS.primary, fontWeight: '600' }}>Période d'essai</h5>
              <p className="text-muted mb-4" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                {error || "Impossible de renouveler une période d'essai : souscrivez un premier abonnement payant depuis la page \"Souscrire un abonnement\"."}
              </p>

              <div className="d-flex justify-content-center gap-3">
                <button
                  className="btn fw-semibold"
                  onClick={() => navigate("/abonnements")}
                  style={{
                    color: COLORS.primary,
                    border: `2px solid ${COLORS.primary}`,
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <i className="bi bi-arrow-left me-2"></i>
                  Retour aux abonnements
                </button>
                <button
                  className="btn fw-semibold"
                  onClick={() => navigate("/abonnements/create")}
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.success,
                    border: `2px solid ${COLORS.success}`,
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <i className="bi bi-bag-check me-2"></i>
                  Souscrire un abonnement
                </button>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>
          <i className="bi bi-arrow-repeat me-2"></i>
          Renouveler l'abonnement
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.secondary }}>
                Dashboard
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/abonnements" style={{ color: COLORS.secondary }}>
                Abonnements
              </Link>
            </li>
            <li className="breadcrumb-item">
              <Link to={`/abonnements/details/${id}`} style={{ color: COLORS.secondary }}>
                Détails
              </Link>
            </li>
            <li className="breadcrumb-item active" style={{ color: COLORS.primary }}>
              Renouveler
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-8 mx-auto">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none',
              borderTop: `4px solid ${COLORS.primary}`
            }}>
              <div className="card-body p-4">
                {/* En-tête */}
                <div className="text-center mb-5">
                  <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ 
                    width: '60px',
                    height: '60px',
                    backgroundColor: '#E8F5E9',
                    color: COLORS.success
                  }}>
                    <i className="bi bi-arrow-repeat fs-3"></i>
                  </div>
                  <h3 style={{ color: COLORS.primary, fontWeight: '600', marginBottom: '8px' }}>
                    Renouvellement d'abonnement
                  </h3>
                  <p className="text-muted" style={{ color: COLORS.darkText }}>
                    {currentProfile === "1" 
                      ? "Renouvelez l'abonnement pour la société"
                      : "Renouvelez votre abonnement"}
                  </p>
                </div>

                {error && (
                  <div className="alert alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#ffebee',
                    borderColor: COLORS.danger,
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.danger}`
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">{error}</div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setError("")}
                      ></button>
                    </div>
                  </div>
                )}
                
                {success && (
                  <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                    backgroundColor: '#e8f5e9',
                    borderColor: COLORS.success,
                    color: '#2e7d32',
                    borderRadius: '8px',
                    borderLeft: `4px solid ${COLORS.success}`,
                    whiteSpace: "pre-line"
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-check-circle me-2" style={{ fontSize: '1.2rem' }}></i>
                      <div className="flex-grow-1">
                        {success}
                      </div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => setSuccess("")}
                      ></button>
                    </div>
                    <div className="mt-3">
                      <button
                        type="button"
                        className="btn btn-sm fw-semibold"
                        onClick={() => navigate("/abonnements")}
                        style={{
                          color: COLORS.white,
                          backgroundColor: COLORS.primary,
                          borderColor: COLORS.primary,
                          borderRadius: '6px',
                          padding: '6px 16px',
                          transition: 'all 0.3s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = COLORS.secondary;
                          e.target.style.borderColor = COLORS.secondary;
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = COLORS.primary;
                          e.target.style.borderColor = COLORS.primary;
                        }}
                      >
                        <i className="bi bi-list-ul me-2"></i>
                        Aller à la liste maintenant
                      </button>
                    </div>
                  </div>
                )}

                {/* Récapitulatif de l'abonnement actuel */}
                <div className="card mb-4" style={{ 
                  border: `1px solid ${COLORS.lightBg}`,
                  borderRadius: '8px',
                  backgroundColor: '#F5F7FA'
                }}>
                  <div className="card-header" style={{ 
                    backgroundColor: COLORS.lightBg,
                    borderBottom: `1px solid ${COLORS.lightBg}`,
                    color: COLORS.primary,
                    fontWeight: '600',
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    <i className="bi bi-info-circle me-2"></i>
                    Abonnement actuel
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <div>
                          <div style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem', marginBottom: '4px' }}>
                            Société
                          </div>
                          <div style={{ color: COLORS.primary, fontWeight: '600', fontSize: '1.1rem' }}>
                            {abonnement.nom_societe}
                            
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6 mb-3">
                        <div>
                          <div style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem', marginBottom: '4px' }}>
                            Offre actuelle
                          </div>
                          <div style={{ color: COLORS.darkText, fontSize: '1rem' }}>
                            {abonnement.offre?.libele_offre || abonnement.libele_abonnement}
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <div>
                          <div style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem', marginBottom: '4px' }}>
                            Date de fin
                          </div>
                          <div style={{ color: COLORS.darkText, fontSize: '1rem' }}>
                            {formatDate(abonnement.date_fin)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <div>
                          <div style={{ color: COLORS.darkText, fontWeight: '500', fontSize: '0.9rem', marginBottom: '4px' }}>
                            Montant actuel
                          </div>
                          <div style={{ color: COLORS.primary, fontWeight: '600', fontSize: '1.1rem' }}>
                            {formatPrix(abonnement.montant)}
                          </div>
                        </div>
                      </div>
                    </div>


                  </div>
                </div>

                {/* Paiement myNITA/Amana en attente : bloque tout renouvellement tant
                    qu'il n'est ni annulé ni (une fois expiré) régénéré — même règle
                    que sur AbonnementsCreate.js. */}
                {paiementEnAttente && (
                  <div className="alert mb-4" style={{
                    backgroundColor: '#FFF3E0',
                    borderLeft: `4px solid ${COLORS.warning}`,
                    borderRadius: '8px'
                  }}>
                    <div className="d-flex">
                      <div className="me-3">
                        <i className="bi bi-hourglass-split fs-4" style={{ color: COLORS.warning }}></i>
                      </div>
                      <div className="flex-grow-1">
                        <h6 className="fw-semibold mb-2" style={{ color: COLORS.darkText }}>
                          {paiementEnAttente.expire ? 'Paiement expiré (24h)' : 'Paiement en attente'}
                        </h6>
                        <p className="mb-2" style={{ color: COLORS.darkText }}>
                          <strong>{paiementEnAttente.libele}</strong> — {new Intl.NumberFormat('fr-FR').format(paiementEnAttente.montant)} FCFA
                          <br />
                          {paiementEnAttente.gateway ? (
                            <span className="small">
                              Référence : <span style={{ fontFamily: 'monospace' }}>{paiementEnAttente.code_achat || paiementEnAttente.reference}</span>
                              {' '}({paiementEnAttente.gateway === 'nita' ? 'NITA' : 'Amana'})
                              {paiementEnAttente.expire
                                ? ' — cette référence a expiré, générez-en une nouvelle.'
                                : ' — valable 24h. Aucun renouvellement possible tant qu\'il est en attente.'}
                            </span>
                          ) : (
                            <span className="small">
                              Aucun moyen de paiement n'a encore été choisi pour ce renouvellement.
                            </span>
                          )}
                        </p>
                        {!paiementEnAttente.expire && paiementEnAttente.gateway && (
                          <p className="small mb-2" style={{ color: COLORS.darkText }}>
                            <i className="bi bi-clock-history me-1"></i>
                            <CompteARebours createdAt={paiementEnAttente.created_at} />
                          </p>
                        )}
                        <div className="d-flex gap-2">
                          {(paiementEnAttente.expire || !paiementEnAttente.gateway) && (
                            <button
                              type="button"
                              className="btn btn-sm fw-semibold"
                              onClick={handleFinaliserPaiementEnAttente}
                              style={{ backgroundColor: COLORS.primary, color: 'white', borderRadius: '6px' }}
                            >
                              <i className="bi bi-arrow-clockwise me-1"></i>
                              {paiementEnAttente.gateway ? 'Générer une nouvelle référence' : 'Choisir un moyen de paiement'}
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-sm fw-semibold"
                            onClick={handleAnnulerPaiementEnAttente}
                            disabled={annulationEnCours}
                            style={{ border: `1px solid ${COLORS.danger}`, color: COLORS.danger, borderRadius: '6px', backgroundColor: 'white' }}
                          >
                            <i className="bi bi-x-circle me-1"></i> {annulationEnCours ? 'Annulation...' : 'Annuler'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sélection de la nouvelle offre */}
                <div className="card mb-4" style={{ 
                  border: `1px solid ${COLORS.lightBg}`,
                  borderRadius: '8px'
                }}>
                  <div className="card-header" style={{ 
                    backgroundColor: COLORS.lightBg,
                    borderBottom: `1px solid ${COLORS.lightBg}`,
                    color: COLORS.primary,
                    fontWeight: '600',
                    fontFamily: "'Inter', sans-serif"
                  }}>
                    <i className="bi bi-box me-2"></i>
                    Choisir la nouvelle offre
                  </div>
                  <div className="card-body">
                    {loadingOffres ? (
                      <div className="text-center py-3">
                        <div className="spinner-border spinner-border-sm" role="status" style={{ color: COLORS.primary }}>
                          <span className="visually-hidden">Chargement...</span>
                        </div>
                        <p className="mt-2" style={{ color: COLORS.darkText, fontSize: '0.9rem' }}>
                          Chargement des offres disponibles...
                        </p>
                      </div>
                    ) : offres.length === 0 ? (
                      <div className="text-center py-3">
                        <div className="mb-2">
                          <i className="bi bi-box text-muted" style={{ fontSize: '2rem' }}></i>
                        </div>
                        <p className="text-muted mb-0">Aucune offre disponible pour le moment.</p>
                      </div>
                    ) : (
                      <div className="row">
                        {offres.map(offre => {
                          const prixMensuel = calculerPrixMensuel(offre.montant, offre.duree_mois);
                          const typeColor = getTypeColor(offre.type_offre);
                          const isSelected = selectedOffre?.id_offre == offre.id_offre;
                          
                          return (
                            <div key={offre.id_offre} className="col-md-6 mb-3">
                              <div 
                                className="card cursor-pointer"
                                onClick={() => handleSelectOffre(offre)}
                                style={{ 
                                  borderRadius: '8px',
                                  border: `2px solid ${isSelected ? COLORS.primary : '#e0e0e0'}`,
                                  backgroundColor: isSelected ? '#E3F2FD' : COLORS.white,
                                  transition: 'all 0.3s ease',
                                  cursor: 'pointer'
                                }}
                                onMouseEnter={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.borderColor = COLORS.accent;
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isSelected) {
                                    e.currentTarget.style.borderColor = '#e0e0e0';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  }
                                }}
                              >
                                <div className="card-body">
                                  <div className="d-flex justify-content-between align-items-start mb-3">
                                    <div>
                                      <h6 className="mb-1" style={{ color: COLORS.primary, fontWeight: '600', fontSize: '1rem' }}>
                                        {offre.libele_offre}
                                      </h6>
                                      <span className="badge fw-semibold" style={{ 
                                        backgroundColor: `${typeColor}15`,
                                        color: typeColor,
                                        padding: '3px 8px',
                                        borderRadius: '4px',
                                        fontSize: '0.75rem'
                                      }}>
                                        <i className={`bi ${getTypeIcon(offre.type_offre)} me-1`}></i>
                                        {offre.type_offre}
                                      </span>
                                    </div>
                                    {isSelected && (
                                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                                        width: '24px',
                                        height: '24px',
                                        backgroundColor: COLORS.success,
                                        color: COLORS.white
                                      }}>
                                        <i className="bi bi-check"></i>
                                      </div>
                                    )}
                                  </div>
                                  
                                  <div className="mb-3">
                                    <div className="fw-bold" style={{ color: COLORS.primary, fontSize: '1.2rem' }}>
                                      {formatPrix(offre.montant)}
                                    </div>
                                    <div className="text-muted small">
                                      {formatDuree(offre.duree_mois)}
                                      <span className="ms-2">
                                        ({formatPrix(prixMensuel)}/mois)
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {offre.description && (
                                    <p className="small text-muted mb-3" style={{ minHeight: '40px' }}>
                                      {offre.description.substring(0, 80)}...
                                    </p>
                                  )}
                                  
                                  <div className="text-center">
                                    <button
                                      className="btn btn-sm fw-semibold"
                                      onClick={() => handleSelectOffre(offre)}
                                      style={{
                                        color: isSelected ? COLORS.white : COLORS.primary,
                                        backgroundColor: isSelected ? COLORS.primary : 'transparent',
                                        border: `1px solid ${isSelected ? COLORS.primary : COLORS.primary}`,
                                        borderRadius: '6px',
                                        padding: '4px 12px',
                                        width: '100%',
                                        transition: 'all 0.3s ease'
                                      }}
                                    >
                                      {isSelected ? (
                                        <>
                                          <i className="bi bi-check-circle me-1"></i>
                                          Sélectionnée
                                        </>
                                      ) : (
                                        <>
                                          <i className="bi bi-cursor me-1"></i>
                                          Sélectionner
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    
                    {selectedOffre && (
                      <div className="mt-4 pt-4 border-top" style={{ borderColor: COLORS.lightBg }}>
                        <div className="alert alert-info" style={{ backgroundColor: '#E3F2FD' }}>
                          <div className="d-flex align-items-center">
                            <i className="bi bi-check-circle me-2" style={{ color: COLORS.primary }}></i>
                            <div>
                              <strong>Offre sélectionnée</strong>
                              <div className="small mt-1">
                                {selectedOffre.libele_offre} - {formatPrix(selectedOffre.montant)} 
                                ({formatDuree(selectedOffre.duree_mois)})
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Détails de paiement (admin seulement) */}
                {currentProfile === "1" && selectedOffre && (
                  <div className="card mb-4" style={{ 
                    border: `1px solid ${COLORS.lightBg}`,
                    borderRadius: '8px'
                  }}>
                    <div className="card-header" style={{ 
                      backgroundColor: COLORS.lightBg,
                      borderBottom: `1px solid ${COLORS.lightBg}`,
                      color: COLORS.primary,
                      fontWeight: '600',
                      fontFamily: "'Inter', sans-serif"
                    }}>
                      <i className="bi bi-credit-card me-2"></i>
                      Détails de paiement
                    </div>
                    <div className="card-body">
                      <div className="mb-4">
                        <label className="form-label fw-semibold mb-3" style={{ color: COLORS.darkText }}>
                          Mode de paiement <span className="text-danger">*</span>
                        </label>
                        
                        <div className="row">
                          <div className="col-md-6">
                            <div 
                              className={`card cursor-pointer ${form.mode_paiement === 'manuel' ? 'border-primary' : ''}`}
                              onClick={() => setForm(prev => ({ ...prev, mode_paiement: 'manuel' }))}
                              style={{ 
                                borderRadius: '8px',
                                border: `2px solid ${form.mode_paiement === 'manuel' ? COLORS.primary : '#e0e0e0'}`,
                                padding: '16px',
                                cursor: 'pointer'
                              }}
                            >
                              <div className="d-flex align-items-center">
                                <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                                  width: '40px',
                                  height: '40px',
                                  backgroundColor: '#E3F2FD',
                                  color: COLORS.primary
                                }}>
                                  <i className="bi bi-receipt"></i>
                                </div>
                                <div>
                                  <div className="fw-semibold">Paiement manuel</div>
                                  <div className="small text-muted">Numéro de reçu bancaire</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {form.mode_paiement === 'manuel' && (
                        <div>
                          <label className="form-label fw-semibold mb-3" style={{ color: COLORS.darkText }}>
                            Numéro de reçu <span className="text-danger">*</span>
                          </label>
                          
                          <div className="input-group">
                            <span className="input-group-text" style={{ 
                              backgroundColor: COLORS.white,
                              borderColor: '#e0e0e0',
                              color: COLORS.secondary
                            }}>
                              <i className="bi bi-receipt-cutoff"></i>
                            </span>
                            <input
                              type="text"
                              name="numero_recu"
                              className="form-control"
                              value={form.numero_recu}
                              onChange={handleChange}
                              placeholder="Ex: RECU-2024-001234"
                              style={{ 
                                borderColor: '#e0e0e0',
                                color: COLORS.darkText
                              }}
                            />
                          </div>
                          <small className="text-muted d-block mt-2">
                            <i className="bi bi-info-circle me-1"></i>
                            Saisissez le numéro du reçu bancaire ou de virement
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Pour gestionnaire : affichage du montant */}
                {currentProfile === "3" && selectedOffre && (
                  <div className="card mb-4" style={{ 
                    border: `1px solid ${COLORS.lightBg}`,
                    borderRadius: '8px',
                    backgroundColor: '#E8F5E9'
                  }}>
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                          width: '40px',
                          height: '40px',
                          backgroundColor: COLORS.success,
                          color: COLORS.white
                        }}>
                          <i className="bi bi-cash-stack"></i>
                        </div>
                        <div>
                          <div style={{ color: '#2e7d32', fontWeight: '600', fontSize: '0.9rem' }}>
                            Montant du renouvellement
                          </div>
                          <div style={{ color: COLORS.primary, fontWeight: '600', fontSize: '1.3rem' }}>
                            {formatPrix(selectedOffre.montant)}
                          </div>
                          <div className="small" style={{ color: '#2e7d32' }}>
                            {formatDuree(selectedOffre.duree_mois)} - {formatPrix(calculerPrixMensuel(selectedOffre.montant, selectedOffre.duree_mois))}/mois
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Boutons d'action */}
                <div className="d-flex justify-content-between mt-5 pt-4 border-top" style={{ borderColor: COLORS.lightBg }}>
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={() => navigate(`/abonnements/details/${id}`)}
                    disabled={loadingRenew || success}
                    style={{
                      color: COLORS.primary,
                      border: `2px solid ${COLORS.primary}`,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 24px',
                      transition: 'all 0.3s ease',
                      opacity: (loadingRenew || success) ? 0.6 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!loadingRenew && !success) {
                        e.target.style.backgroundColor = COLORS.primary;
                        e.target.style.color = COLORS.white;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loadingRenew && !success) {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = COLORS.primary;
                      }
                    }}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Annuler
                  </button>
                  
                  <button
                    type="button"
                    className="btn fw-semibold"
                    onClick={handleRenew}
                    disabled={loadingRenew || !selectedOffre || (currentProfile === "1" && form.mode_paiement === "manuel" && !form.numero_recu) || success || !!paiementEnAttente}
                    style={{
                      color: COLORS.white,
                      backgroundColor: success ? COLORS.info : COLORS.success,
                      borderColor: success ? COLORS.info : COLORS.success,
                      borderRadius: '8px',
                      padding: '10px 32px',
                      transition: 'all 0.3s ease',
                      opacity: (loadingRenew || !selectedOffre || (currentProfile === "1" && form.mode_paiement === "manuel" && !form.numero_recu) || success || !!paiementEnAttente) ? 0.6 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!loadingRenew && selectedOffre && !(currentProfile === "1" && form.mode_paiement === "manuel" && !form.numero_recu) && !success && !paiementEnAttente) {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 12px rgba(76, 175, 80, 0.2)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!loadingRenew) {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = 'none';
                      }
                    }}
                  >
                    {loadingRenew ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Renouvellement en cours...
                      </>
                    ) : success ? (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Renouvellement réussi
                      </>
                    ) : (
                      <>
                        <i className="bi bi-arrow-repeat me-2"></i>
                        Renouveler l'abonnement
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .form-label,
        .alert strong {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .form-control,
        .alert,
        small {
          font-family: 'Roboto', sans-serif;
        }
        
        .form-control:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .cursor-pointer {
          cursor: pointer;
        }
        
        .btn:disabled {
          cursor: not-allowed;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > button {
            width: 100%;
          }
        }
      `}</style>

      <PaiementNitaAmanaModal
        show={showPaiementModal}
        idPaiement={paiementEnCours?.id_paiement}
        montant={paiementEnCours?.montant}
        onClose={() => {
          setShowPaiementModal(false);
          setError("Paiement mis de côté : il reste en attente (valable 24h). Vous pouvez le finaliser ou l'annuler ci-dessus.");
          if (id) fetchAbonnementDetails();
        }}
        onSuccess={() => {
          setShowPaiementModal(false);
          setSuccess(`✅ Paiement confirmé, abonnement renouvelé !\n\n📋 Nouvelle référence: ${paiementEnCours?.newAbonnementId}\n\nRedirection vers la liste des abonnements...`);
          setTimeout(() => navigate("/abonnements"), 3000);
        }}
      />
    </Layout>
  );
};

export default AbonnementsRenew;